import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/UserStore'
import { useRoute } from 'vue-router'

/**
 * useNewDesign — reactive gate for the "new design" rollout.
 *
 * Single source of truth is `user.data.about.new_design` (surfaced by the
 * `UserStore.isNewDesign` getter). Everything that belongs to the new design
 * — new views, toolbars, action-slot buttons, layouts — must be gated on this
 * so the whole feature can be flipped from one place.
 *
 *   const { isNewDesign } = useNewDesign()
 *   <NewThing v-if="isNewDesign" />
 *   <OldThing v-else />
 *
 * Reactive: when the toggle flips `user.data.about.new_design`, every consumer
 * updates automatically.
 */
export function useNewDesign () {
  const userStore = useUserStore()
  const { isNewDesign, newDesignPromptDismissed, newDesignPromptDismissedFinal, newDesignTourSeen } = storeToRefs(userStore)
  const route = useRoute()

  // Merge-patch `user.data.about` with the given partial. Spreads existing
  // `data` + `about` so no sibling fields are dropped (the PATCH replaces the
  // whole `data` object server-side).
  const patchAbout = (partial) => {
    const data = userStore.user?.data || {}
    const about = data.about || {}
    return userStore.updateUser({ data: { ...data, about: { ...about, ...partial } } })
  }

  /**
   * Flip the design mode by persisting `user.data.about.new_design`. After it
   * resolves we do a full reload so AG Grid column defs, route guards and layout
   * chrome all re-evaluate cleanly under the new mode.
   */
  const setNewDesign = async (value) => {
    const optingIn = value === true

    // Opting OUT is a decision, not a "not now". The welcome modal's stage-1
    // condition is `!new_design && !dismissed && !dismissed_final`, which is
    // EXACTLY the state you land in after switching to legacy — so a user who
    // opted in from the sidebar / user-menu (neither of which sets a dismissal)
    // got the "Welcome to Inventory Beta" invite thrown at them the instant
    // they left. Record the choice in the same patch; the explicit
    // "Try Inventory Beta" button is still there whenever they want back in.
    await patchAbout(optingIn
      ? { new_design: true }
      : { new_design: false, new_design_dismissed: true, new_design_dismissed_final: true })

    window.location.href = route.fullPath
  }

  /**
   * Persist the FIRST "Maybe later" dismissal. No reload — the user stays on the
   * old design; the stage-2 follow-up prompt appears after a delay.
   */
  const dismissNewDesignPrompt = () => patchAbout({ new_design_dismissed: true })

  /**
   * Persist the SECOND (final) "Maybe later" dismissal. No welcome modal is ever
   * shown again after this.
   */
  const dismissNewDesignPromptFinal = () => patchAbout({ new_design_dismissed_final: true })

  /** Persist that the first-run guided tour was completed/skipped. */
  const markNewDesignTourSeen = () => patchAbout({ new_design_tour_seen: true })

  return {
    /** `true` when the user has opted into the new design. */
    isNewDesign,
    /** Convenience inverse for `v-else`-free old-design branches. */
    isOldDesign: computed(() => !isNewDesign.value),
    /** `true` once the first welcome prompt has been dismissed. */
    newDesignPromptDismissed,
    /** `true` once the follow-up prompt has been dismissed (final). */
    newDesignPromptDismissedFinal,
    /** Stage 1: old design, nothing dismissed yet. */
    shouldPromptNewDesign: computed(() =>
      !isNewDesign.value && !newDesignPromptDismissed.value && !newDesignPromptDismissedFinal.value),
    /** Stage 2: old design, first dismissed, follow-up not yet dismissed. */
    shouldPromptNewDesignSecond: computed(() =>
      !isNewDesign.value && newDesignPromptDismissed.value && !newDesignPromptDismissedFinal.value),
    /** `true` once the first-run guided tour has been seen. */
    newDesignTourSeen,
    /** Run the first-run tour: new design, on Inventory, tour not yet seen. */
    shouldRunNewDesignTour: computed(() =>
      isNewDesign.value && !newDesignTourSeen.value),
    /** Persist the design-mode flag (`true`/`false`) then reload into it. */
    setNewDesign,
    /** Persist the first "Maybe later" dismissal (no reload). */
    dismissNewDesignPrompt,
    /** Persist the final "Maybe later" dismissal (no reload). */
    dismissNewDesignPromptFinal,
    /** Persist that the first-run guided tour was completed/skipped. */
    markNewDesignTourSeen,
  }
}
